import React, { useState, useEffect } from 'react';
import InstanceCard from './InstanceCard';
import Layout from './Layout';
import ConsoleViewer from './ConsoleViewer';
import InstanceMetricsPanel from './InstanceMetricsPanel';
import { usePlatformConfig, nodeHostname, sshCommand } from '../hooks/usePlatformConfig';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function ModernDashboard() {
  const [instances, setInstances] = useState([]);
  const { config } = usePlatformConfig();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid'); // 'grid' or 'list'
  const [filter, setFilter] = useState('all'); // 'all', 'running', 'stopped'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdInstance, setCreatedInstance] = useState(null); // holds new instance info after creation
  const [creating, setCreating] = useState(false);
  const [consoleInstanceId, setConsoleInstanceId] = useState(null);
  const [metricsInstance, setMetricsInstance] = useState(null);

  const token = localStorage.getItem('access_token');

  useEffect(() => {
    fetchInstances();
    const interval = setInterval(fetchInstances, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchInstances = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/instances`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setInstances(data.instances || []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch instances:', err);
      setLoading(false);
    }
  };

  const [domainInstance, setDomainInstance] = useState(null);

  const handleAction = async (action, instanceId) => {
    console.log(`Action: ${action} on instance: ${instanceId}`);
    
    if (action === 'console') {
      setConsoleInstanceId(instanceId);
    } else if (action === 'metrics') {
      const inst = instances.find(i => i.id === instanceId);
      setMetricsInstance(inst || null);
    } else if (action === 'domain') {
      const inst = instances.find(i => i.id === instanceId);
      setDomainInstance(inst || null);
    } else if (action === 'stop') {
      await fetch(`${API_URL}/v1/instances/${instanceId}/actions/stop`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchInstances();
    } else if (action === 'start') {
      await fetch(`${API_URL}/v1/instances/${instanceId}/actions/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchInstances();
    } else if (action === 'delete') {
      const instance = instances.find(i => i.id === instanceId);
      const confirmMsg = `Are you sure you want to delete "${instance?.name}"?\n\nThis action cannot be undone.`;
      
      if (window.confirm(confirmMsg)) {
        try {
          const response = await fetch(`${API_URL}/v1/instances/${instanceId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            // Optimistically remove from UI
            setInstances(instances.filter(i => i.id !== instanceId));
            // Then refresh to be sure
            setTimeout(fetchInstances, 1000);
          } else {
            const error = await response.json();
            alert(`Failed to delete instance: ${error.error || 'Unknown error'}`);
          }
        } catch (err) {
          console.error('Delete error:', err);
          alert('Failed to delete instance. Please try again.');
        }
      }
    }
  };

  const filteredInstances = instances.filter(instance => {
    if (filter === 'all') return true;
    return instance.status === filter;
  });

  const stats = {
    total: instances.length,
    running: instances.filter(i => i.status === 'running').length,
    stopped: instances.filter(i => i.status === 'stopped').length,
  };

  return (
    <Layout userType="customer" onCreateInstance={() => setShowCreateModal(true)}>
      {/* Stats Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-gray-400 text-sm">Total Instances</div>
            <div className="text-3xl font-bold text-white">{stats.total}</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-gray-400 text-sm">Running</div>
            <div className="text-3xl font-bold text-green-400">{stats.running}</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-gray-400 text-sm">Stopped</div>
            <div className="text-3xl font-bold text-gray-400">{stats.stopped}</div>
          </div>
          <div className="bg-slate-800 rounded-lg p-4">
            <div className="text-gray-400 text-sm">Monthly Cost</div>
            <div className="text-3xl font-bold text-blue-400">$24.00</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex space-x-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-md ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-gray-400'}`}
            >
              All ({instances.length})
            </button>
            <button
              onClick={() => setFilter('running')}
              className={`px-4 py-2 rounded-md ${filter === 'running' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-gray-400'}`}
            >
              Running ({stats.running})
            </button>
            <button
              onClick={() => setFilter('stopped')}
              className={`px-4 py-2 rounded-md ${filter === 'stopped' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-gray-400'}`}
            >
              Stopped ({stats.stopped})
            </button>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={() => setView('grid')}
              className={`p-2 rounded-md ${view === 'grid' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-gray-400'}`}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setView('list')}
              className={`p-2 rounded-md ${view === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-gray-400'}`}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Instance Grid */}
        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading instances...</div>
        ) : filteredInstances.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-4">No instances yet</div>
            <button 
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md"
            >
              Create Your First Instance
            </button>
          </div>
        ) : (
          <div className={view === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
            : 'space-y-4'
          }>
            {filteredInstances.map(instance => (
              <InstanceCard
                key={instance.id}
                instance={instance}
                onAction={handleAction}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Instance Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-6 max-w-lg w-full mx-4 max-h-screen overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Create New Instance</h3>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              setCreating(true);
              
              const formData = new FormData(e.target);
              const useCustom = formData.get('use_custom') === 'on';
              const payload = {
                name: formData.get('name'),
                os_image: formData.get('os_image'),
                instance_type: useCustom ? 'custom' : formData.get('instance_type'),
                region: formData.get('region'),
              };
              if (useCustom) {
                payload.custom_vcpus = parseInt(formData.get('custom_vcpus'));
                payload.custom_ram_gb = parseInt(formData.get('custom_ram_gb'));
                payload.custom_storage_gb = parseInt(formData.get('custom_storage_gb'));
              }
              try {
                const response = await fetch(`${API_URL}/v1/instances`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(payload)
                });
                
                if (response.ok) {
                  const data = await response.json();
                  setShowCreateModal(false);
                  setCreatedInstance(data);
                  fetchInstances();
                } else {
                  const data = await response.json();
                  alert('Failed to create instance: ' + (data.error || 'Unknown error'));
                }
              } catch (err) {
                alert('Error: ' + err.message);
              } finally {
                setCreating(false);
              }
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                  <input
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white"
                    placeholder="my-server"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Operating System</label>
                  <select name="os_image" required className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white">
                    <option value="ubuntu-22.04">Ubuntu 22.04 LTS</option>
                    <option value="ubuntu-24.04">Ubuntu 24.04 LTS</option>
                    <option value="debian-12">Debian 12</option>
                    <option value="rocky-9">Rocky Linux 9</option>
                  </select>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-300">Plan</label>
                    <label className="flex items-center text-sm text-gray-400 cursor-pointer">
                      <input type="checkbox" name="use_custom" className="mr-2"
                        onChange={(e) => {
                          const customFields = document.getElementById('custom-size-fields');
                          const standardFields = document.getElementById('standard-size-fields');
                          if (e.target.checked) { customFields.style.display='block'; standardFields.style.display='none'; }
                          else { customFields.style.display='none'; standardFields.style.display='block'; }
                        }} />
                      Custom size
                    </label>
                  </div>
                  <div id="standard-size-fields">
                    <select name="instance_type" className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white">
                      <option value="starter">Starter — 1 vCPU, 1GB RAM, 25GB — $6/mo</option>
                      <option value="basic">Basic — 1 vCPU, 2GB RAM, 50GB — $12/mo</option>
                      <option value="standard" selected>Standard — 2 vCPU, 4GB RAM, 80GB — $24/mo</option>
                      <option value="performance">Performance — 4 vCPU, 8GB RAM, 160GB — $48/mo</option>
                      <option value="advanced">Advanced — 8 vCPU, 16GB RAM, 320GB — $96/mo</option>
                    </select>
                  </div>
                  <div id="custom-size-fields" style={{display:'none'}}>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">vCPUs</label>
                        <input type="number" name="custom_vcpus" min="1" max="256" defaultValue="2"
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">RAM (GB)</label>
                        <input type="number" name="custom_ram_gb" min="1" max="2048" defaultValue="4"
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Storage (GB)</label>
                        <input type="number" name="custom_storage_gb" min="10" max="50000" defaultValue="80"
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white" />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Region</label>
                  <select name="region" required className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white">
                    <option value="us-east">US East</option>
                    <option value="us-west">US West</option>
                    <option value="eu-west">EU West</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button type="button" onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-slate-700 text-white rounded-md hover:bg-slate-600" disabled={creating}>
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Instance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Domain Connection Modal */}
      {domainInstance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-6 max-w-xl w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">🌐 Connect Your Domain</h3>
              <button onClick={() => setDomainInstance(null)} className="text-gray-400 hover:text-white">✕</button>
            </div>

            <div className="space-y-4">
              {/* Auto hostname */}
              <div className="bg-slate-900 rounded-lg p-4">
                <p className="text-xs text-gray-400 mb-2">Your VM's built-in hostname (ready to use now):</p>
                <div className="flex items-center space-x-2">
                  <code className="text-blue-400 font-mono text-sm flex-1">
                    {nodeHostname(domainInstance.id, config.nodes_domain)}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(nodeHostname(domainInstance.id, config.nodes_domain))}
                    className="px-2 py-1 bg-slate-600 hover:bg-slate-500 text-white text-xs rounded"
                  >Copy</button>
                </div>
              </div>

              {/* Custom domain instructions */}
              <div>
                <p className="text-sm font-medium text-white mb-2">To use your own domain (e.g. mysite.com):</p>
                <div className="bg-slate-900 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Step 1 — Add a CNAME record at your domain registrar:</p>
                    <div className="bg-slate-800 rounded p-2 font-mono text-xs">
                      <div className="grid grid-cols-3 gap-2 text-gray-400 mb-1">
                        <span>Type</span><span>Name</span><span>Value</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-white">
                        <span>CNAME</span>
                        <span>@ or www</span>
                        <span className="text-blue-400">{nodeHostname(domainInstance.id, config.nodes_domain)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 mb-1">Step 2 — Or use our nameservers for full DNS management:</p>
                    <div className="bg-slate-800 rounded p-2 font-mono text-xs space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-white">{config.ns1_hostname}</span>
                        <button onClick={() => navigator.clipboard.writeText(config.ns1_hostname)} className="text-gray-500 hover:text-gray-300 text-xs">Copy</button>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white">{config.ns2_hostname}</span>
                        <button onClick={() => navigator.clipboard.writeText(config.ns2_hostname)} className="text-gray-500 hover:text-gray-300 text-xs">Copy</button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Point your domain's nameservers to these at your registrar (GoDaddy, Namecheap, etc.)</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 mb-1">Step 3 — Make sure your VM is running a web server on port 80:</p>
                    <div className="bg-slate-800 rounded p-2 font-mono text-xs text-green-400">
                      sudo apt install nginx -y
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500">DNS propagation typically takes 5-30 minutes. HTTPS support coming soon.</p>
            </div>

            <button
              onClick={() => setDomainInstance(null)}
              className="mt-4 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Instance Created Modal — shows password once */}
      {createdInstance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-green-900 rounded-full flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-white">Instance Created!</h3>
            </div>

            <div className="bg-slate-900 rounded-lg p-4 mb-4 space-y-3">
              <div>
                <p className="text-xs text-gray-400 mb-1">Instance Name</p>
                <p className="text-white font-mono text-sm">{createdInstance.name}</p>
              </div>
              {createdInstance.initial_password && (
                <div>
                  <p className="text-xs text-gray-400 mb-1">⚠️ Initial Password — save this now, it won't be shown again</p>
                  <div className="flex items-center space-x-2">
                    <code className="bg-slate-700 text-yellow-300 px-3 py-2 rounded font-mono text-sm flex-1">
                      {createdInstance.initial_password}
                    </code>
                    <button
                      onClick={() => navigator.clipboard.writeText(createdInstance.initial_password)}
                      className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-white text-xs rounded"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-400 mb-1">SSH Command (once running)</p>
                <code className="bg-slate-700 text-green-400 px-3 py-2 rounded font-mono text-xs block">
                  ssh -p PORT ubuntu@{config.ssh_hostname}
                </code>
                <p className="text-xs text-gray-500 mt-1">Port will appear on the instance card once provisioning completes (~60s).</p>
              </div>
            </div>

            <button
              onClick={() => setCreatedInstance(null)}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Console Viewer */}
      {consoleInstanceId && (
        <ConsoleViewer
          instanceId={consoleInstanceId}
          onClose={() => setConsoleInstanceId(null)}
        />
      )}

      {/* Instance Metrics Panel */}
      {metricsInstance && (
        <InstanceMetricsPanel
          instance={metricsInstance}
          onClose={() => setMetricsInstance(null)}
        />
      )}
    </Layout>
  );
}

export default ModernDashboard;
